import {
  type QueryKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import cloneDeep from 'lodash/cloneDeep';

type ListContext<T> = { previous?: T[] };

/** Create/update/delete mutations that patch a cached list up front and roll it back on error. */
export function useOptimisticListMutations<
  T extends { id: string },
  TCreated,
  TUpdated,
  TDeleted,
>({
  listKey,
  createFn,
  updateFn,
  deleteFn,
  onCreated,
  onUpdated,
  onDeleted,
}: {
  listKey: QueryKey;
  createFn: (entry: T) => Promise<TCreated>;
  updateFn: (id: string, data: Partial<T>) => Promise<TUpdated>;
  deleteFn: (id: string) => Promise<TDeleted>;
  onCreated?: (saved: TCreated, entry: T) => unknown;
  onUpdated?: (
    saved: TUpdated,
    variables: { id: string; data: Partial<T> }
  ) => unknown;
  onDeleted?: (id: string) => unknown;
}) {
  const queryClient = useQueryClient();

  const patchList = async (
    updater: (entries: T[]) => T[]
  ): Promise<ListContext<T>> => {
    await queryClient.cancelQueries({ queryKey: listKey });
    const previous = queryClient.getQueryData<T[]>(listKey);
    if (previous) {
      queryClient.setQueryData<T[]>(listKey, updater(cloneDeep(previous)));
    }
    return { previous };
  };

  const rollback = (context: ListContext<T> | undefined) => {
    if (context?.previous) queryClient.setQueryData(listKey, context.previous);
  };

  const create = useMutation({
    mutationFn: createFn,
    onMutate: (entry: T) => patchList((entries) => [...entries, entry]),
    onSuccess: (saved, entry) => onCreated?.(saved, entry),
    onError: (_error, _entry, context) => rollback(context),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<T> }) =>
      updateFn(id, data),
    onMutate: (variables) =>
      patchList((entries) =>
        entries.map((entry) =>
          entry.id === variables.id ? { ...entry, ...variables.data } : entry
        )
      ),
    onSuccess: (saved, variables) => onUpdated?.(saved, variables),
    onError: (_error, _variables, context) => rollback(context),
  });

  const remove = useMutation({
    mutationFn: deleteFn,
    onMutate: (id: string) =>
      patchList((entries) => entries.filter((entry) => entry.id !== id)),
    onSuccess: (_result, id) => onDeleted?.(id),
    onError: (_error, _id, context) => rollback(context),
  });

  return { create, update, remove };
}
